import { IOGateway } from '@interopio/gateway';
import getLogger from '../logger.ts';
import type { GatewayServer  } from '../../gateway-server.ts';
import { processGatewayConfig } from './config.ts';

const log = getLogger('gateway-manager');

function generateRandomGatewayId(): string {
    return globalThis.crypto.randomUUID().replaceAll('-', '');
}


export interface GatewayManagerConfig {
    /**
     * Base gateway configuration to use for all gateway instances.
     */
    baseConfig: IOGateway.GatewayConfig & {
        metrics?: IOGateway.MetricsConfig & { enabled?: boolean },
        mesh?: IOGateway.MeshConfig & { enabled?: boolean }
    };

    /**
     * Gateway scope - determines how gateway instances are managed:
     * - 'principal': Each authenticated principal gets their own gateway instance.
     *                A default (shared) gateway instance handles all unauthenticated connections.
     * - 'singleton': All connections share the same gateway instance
     *
     * Default: 'principal'
     */
    scope?: 'singleton' | 'principal';
}

/**
 * Manages multiple Gateway instances - one default and optionally one per authenticated principal.
 * Each gateway instance gets a unique node ID to ensure proper isolation.
 */
export class GatewayManager implements GatewayServer.ScopedGateway {
    readonly #defaultGatewayId: string;
    readonly #defaultGateway: IOGateway.Gateway;
    readonly #managedGateways = new Map<string, IOGateway.Gateway>();
    readonly #principalToGatewayId = new Map<string, string>();
    readonly #config: GatewayManagerConfig;
    #started = false;
    #environment?: { endpoint?: string };

    constructor(config: GatewayManagerConfig) {
        this.#config = {
            baseConfig: processGatewayConfig(config.baseConfig),
            scope: config.scope ?? 'principal'
        }

        // Use configured gateway ID or generate a random one for the default gateway
        this.#defaultGatewayId = config.baseConfig.node ?? generateRandomGatewayId();

        if (log.enabledFor('debug')) {
            log.debug(`creating default gateway with gateway id: ${this.#defaultGatewayId}`);
        }

        this.#defaultGateway = IOGateway.Factory({...this.#config.baseConfig, node: this.#defaultGatewayId});
    }

    /**
     * Starts the default gateway.
     */
    async start(environment?: { endpoint?: string }): Promise<this> {
        if (this.#started) {
            return this;
        }

        // Store environment for use with per-principal gateways
        this.#environment = environment;

        log.debug('starting default gateway');
        await this.#defaultGateway.start(environment);
        this.#started = true;
        return this;
    }

    /**
     * Gets a gateway instance for the given principal.
     * If scope is 'singleton' or no principal is provided, returns the default gateway.
     * Otherwise, creates a new gateway instance for the principal if it doesn't exist.
     */
    async getGateway(principal?: string): Promise<IOGateway.Gateway> {
        // If scope is singleton or no principal, use default
        if (this.#config.scope === 'singleton' || !principal) {
            return this.#defaultGateway;
        }

        // Check if we already have a gateway for this principal
        const gatewayId = this.#principalToGatewayId.get(principal);
        let gateway = gatewayId ? this.#managedGateways.get(gatewayId) : undefined;

        if (!gateway) {
            if (log.enabledFor('debug')) {
                log.debug(`no existing gateway for principal '${principal}', creating new one`);
            }
            gateway = await this.#createPrincipalGateway(principal);
        } else {
            if (log.enabledFor('debug')) {
                log.debug(`reusing existing gateway for principal '${principal}'`);
            }
        }

        return gateway;
    }

    /**
     * Creates a new gateway instance for a specific principal with a unique gateway ID.
     */
    async #createPrincipalGateway(principal: string): Promise<IOGateway.Gateway> {
        const gatewayId = generateRandomGatewayId();
        const config = {
            ...this.#config.baseConfig,
            node: gatewayId
         };

        if (log.enabledFor('debug')) {
            log.debug(`creating gateway for principal '${principal}' with gateway id: ${config.node}`);
        }
        const gateway = IOGateway.Factory(config);

        // Track the principal-to-gateway ID mapping
        this.#principalToGatewayId.set(principal, gatewayId);
        this.#managedGateways.set(gatewayId, gateway);

        // Start the gateway with the same environment as the default gateway
        await gateway.start(this.#environment);

        return gateway;
    }

    // ScopedGateway interface methods

    /**
     * Get all gateway instances including the default.
     */
    getGateways(): Map<string, IOGateway.Gateway> {
        const allGateways = new Map<string, IOGateway.Gateway>(this.#managedGateways);
        allGateways.set(this.#defaultGatewayId, this.#defaultGateway);
        return allGateways;
    }

    /**
     * Gets information about specific or all active gateways.
     * - If no gatewayId is provided: returns aggregated info for all gateways (default + managed).
     * - If default gateway ID is provided: returns info only for the default gateway.
     * - If a non-default gateway ID is provided: returns info only for that specific gateway. If not found, throws an error.
     */
    info(gatewayId?: string): Record<string, unknown> {
        // Case 1: Specific non-default gateway ID provided
        if (gatewayId && this.#defaultGatewayId !== gatewayId) {
            const gateway = this.#managedGateways.get(gatewayId);
            if (gateway) {
                return gateway.info();
            } else {
                throw new Error(`no gateway found with ID: ${gatewayId}`);
            }
        }

        // Case 2: Default gateway ID explicitly provided - return only default info
        if (gatewayId === this.#defaultGatewayId) {
            return this.#defaultGateway.info();
        }

        // Case 3: No gateway ID provided - return aggregated info
        return {
            ...this.#defaultGateway.info(),
            managedGateways: this.#managedGateways.size,
            scope: this.#config.scope
        };
    }

    /**
     * Stops specific or all gateway instances.
     * - If no gatewayId is provided: stops all gateways (default + managed), returning the stopped default.
     * - If default gateway ID is provided: stops only the default gateway, leaving managed gateways running.
     * - If a non-default gateway ID is provided: stops only that specific gateway. If not found, throws an error.
     * @param gatewayId - ID of the gateway to stop.
     * @returns stopped gateway instance.
     */
    async stop(gatewayId?: string): Promise<IOGateway.Gateway> {
        // Case 1: Specific non-default gateway ID provided
        if (gatewayId && this.#defaultGatewayId !== gatewayId) {
            const gateway = this.#managedGateways.get(gatewayId);
            if (gateway) {
                log.info(`stopping gateway with ID: ${gatewayId}`);
                await gateway.stop();
                this.#managedGateways.delete(gatewayId);

                // Remove principal mapping for this gateway
                for (const [principal, gId] of this.#principalToGatewayId.entries()) {
                    if (gId === gatewayId) {
                        this.#principalToGatewayId.delete(principal);
                        break;
                    }
                }

                return gateway;
            } else {
                throw new Error(`no gateway found with ID: ${gatewayId}`);
            }
        }

        // Case 2: Default gateway ID explicitly provided - stop only default
        if (gatewayId === this.#defaultGatewayId) {
            log.debug('stopping default gateway (managed gateways will continue running)');
            await this.#defaultGateway.stop();
            this.#started = false;
            return this.#defaultGateway;
        }

        // Case 3: No gateway ID provided - stop all gateways
        log.info(`stopping all gateways (1 default + ${this.#managedGateways.size} managed)`);

        // Stop all managed gateways
        for (const [id, gateway] of this.#managedGateways.entries()) {
            if (log.enabledFor('debug')) {
                log.debug(`stopping gateway with ID: ${id}`);
            }
            await gateway.stop();
        }
        this.#managedGateways.clear();
        this.#principalToGatewayId.clear();

        log.debug('stopping default gateway');
        await this.#defaultGateway.stop();
        this.#started = false;
        return this.#defaultGateway;
    }


    /**
     * Get the gateway ID for a specific principal.
     */
    getPrincipalGatewayId(principal: string): string | undefined {
        return this.#principalToGatewayId.get(principal);
    }

    /**
     * Get all principal-to-gateway ID mappings.
     */
    getPrincipalGatewayIds(): Map<string, string> {
        return new Map(this.#principalToGatewayId);
    }


    // Gateway interface delegation to defaultGateway

    /**
     * Get the default gateway instance.
     */
    getDefaultGateway(): IOGateway.Gateway {
        return this.#defaultGateway;
    }

    /**
     * Create a gateway client with the specified handler.
     * Delegates to the default gateway.
     */
    client<T = IOGateway.Message>(
        handler: (this: IOGateway.GatewayClient<T>, msg: T) => void,
        opts?: IOGateway.GatewayClientOptions<T>
    ): IOGateway.GatewayClient<T> {
        return this.#defaultGateway.client(handler, opts);
    }

    /**
     * Connect to the gateway with the specified handler.
     * Delegates to the default gateway.
     * @deprecated
     */
    async connect(
        handler: (client: IOGateway.GatewayClient, msg: IOGateway.Message) => void
    ): Promise<IOGateway.GatewayClient> {
        return this.#defaultGateway.connect(handler);
    }

    /**
     * Gets the number of active principal gateways.
     */
    getPrincipalCount(): number {
        return this.#managedGateways.size;
    }
}
